/**
 * Persistence and delivery helpers for insight crawler + automated report emails.
 */
import { supabase, DataService } from "./supabase";
import { DataJoinService } from "./dataJoins";
import { CalculationService } from "./calculationService";
import { buildInsightsPdfBlob } from "./exportService";
import { runInsightCrawler } from "./insightCrawlerService";
import type { Transaction } from "../types";
import type { GradingOrder } from "./dataJoins";
import type { AutomatedReportJob, InsightPdfItem } from "../types/insights";
import { AUTOMATED_REPORTS_PREF_KEY } from "../types/insights";

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk) as unknown as number[]);
  }
  return btoa(binary);
}

export async function loadAutomatedReportsConfig(ds: DataService): Promise<AutomatedReportJob[]> {
  const raw = await ds.getUserPreference(AUTOMATED_REPORTS_PREF_KEY);
  if (!raw || typeof raw !== "object") return [];
  const jobs = (raw as { jobs?: AutomatedReportJob[] }).jobs;
  return Array.isArray(jobs) ? jobs : [];
}

export async function saveAutomatedReportsConfig(ds: DataService, jobs: AutomatedReportJob[]): Promise<void> {
  await ds.setUserPreference(AUTOMATED_REPORTS_PREF_KEY, { jobs });
}

function insightsToHtml(items: InsightPdfItem[]): string {
  const blocks = items
    .map(
      (i) => `
    <div style="margin-bottom:20px;border-bottom:1px solid #eee;padding-bottom:16px;">
      <h2 style="margin:0 0 8px;font-size:16px;">${escapeHtml(i.title)}</h2>
      <p style="margin:0 0 8px;color:#444;">${escapeHtml(i.summary)}</p>
      <ul style="margin:0;padding-left:20px;">
        ${i.actions.map((a) => `<li>${escapeHtml(a)}</li>`).join("")}
      </ul>
    </div>`
    )
    .join("");
  return `<!DOCTYPE html><html><body style="font-family:system-ui,sans-serif;max-width:640px;">${blocks}</body></html>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function dashboardSheetsToHtml(dashboardName: string, sheets: { name: string; rows: Record<string, unknown>[] }[]): string {
  const parts = sheets.map((sheet) => {
    const keys =
      sheet.rows.length > 0
        ? Object.keys(sheet.rows[0])
        : [];
    const head = keys.map((k) => `<th style="text-align:left;padding:4px 8px;border:1px solid #ddd;">${escapeHtml(k)}</th>`).join("");
    const bodyRows = sheet.rows.slice(0, 50).map((row) => {
      const cells = keys
        .map((k) => `<td style="padding:4px 8px;border:1px solid #ddd;">${escapeHtml(String(row[k] ?? ""))}</td>`)
        .join("");
      return `<tr>${cells}</tr>`;
    });
    return `
      <h2 style="margin:24px 0 8px;font-size:16px;">${escapeHtml(sheet.name)}</h2>
      <table style="border-collapse:collapse;width:100%;font-size:12px;margin-bottom:16px;">
        <thead><tr>${head}</tr></thead>
        <tbody>${bodyRows.join("")}</tbody>
      </table>
    `;
  });
  return `<!DOCTYPE html><html><body style="font-family:system-ui,sans-serif;">
    <h1 style="font-size:18px;">${escapeHtml(dashboardName)}</h1>
    <p style="color:#666;">First 50 rows per analysis (see PDF for same tabular export).</p>
    ${parts.join("")}
  </body></html>`;
}

export async function buildDashboardPdfBlob(
  dashboardName: string,
  sheets: { name: string; rows: Record<string, unknown>[] }[]
): Promise<Uint8Array> {
  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");
  const pdf = new jsPDF("p", "mm", "a4");
  const margin = 14;
  let first = true;
  for (const sheet of sheets) {
    if (!first) pdf.addPage();
    first = false;
    pdf.setFontSize(14);
    pdf.text(`${dashboardName} — ${sheet.name}`, margin, margin + 6);
    const keys = sheet.rows.length > 0 ? Object.keys(sheet.rows[0]) : [];
    const head = [keys];
    const body = sheet.rows.slice(0, 80).map((row) => keys.map((k) => String(row[k] ?? "")));
    autoTable(pdf, {
      startY: margin + 12,
      head,
      body,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7 },
      headStyles: { fillColor: [66, 139, 202] },
    });
  }
  return new Uint8Array(pdf.output("arraybuffer"));
}

/**
 * Enrich transactions the same way as reporting charts for consistent insight math.
 */
export function getEnrichedTransactionsForInsights(
  transactions: Transaction[],
  gradingOrders: GradingOrder[]
): Transaction[] {
  const joined = DataJoinService.joinTransactionsWithGradingOrders(transactions, gradingOrders);
  return CalculationService.enrichTransactions(joined, gradingOrders);
}

export async function sendInsightsEmail(args: {
  userEmail: string;
  transactions: Transaction[];
  gradingOrders: GradingOrder[];
}): Promise<void> {
  const enriched = getEnrichedTransactionsForInsights(args.transactions, args.gradingOrders);
  const insights = runInsightCrawler(enriched);
  const items: InsightPdfItem[] = insights.map((i) => ({
    title: i.title,
    summary: i.summary,
    actions: i.actions,
  }));
  const pdfBytes = await buildInsightsPdfBlob("Pink Dolphin — Insight digest", items);
  const b64 = arrayBufferToBase64(pdfBytes.buffer);

  const { error } = await supabase.functions.invoke("send-reporting-email", {
    body: {
      to: args.userEmail,
      subject: "Your Pink Dolphin insight digest",
      html: insightsToHtml(items),
      attachments: [{ filename: "insight-digest.pdf", content: b64 }],
    },
  });
  if (error) throw new Error(error.message || "Failed to send insight email");
}

export async function sendDashboardReportEmail(args: {
  userEmail: string;
  dashboardName: string;
  sheets: { name: string; rows: Record<string, unknown>[] }[];
}): Promise<void> {
  const pdfBytes = await buildDashboardPdfBlob(args.dashboardName, args.sheets);
  const b64 = arrayBufferToBase64(pdfBytes.buffer);
  const { error } = await supabase.functions.invoke("send-reporting-email", {
    body: {
      to: args.userEmail,
      subject: `Pink Dolphin — Dashboard: ${args.dashboardName}`,
      html: dashboardSheetsToHtml(args.dashboardName, args.sheets),
      attachments: [{ filename: "dashboard-report.pdf", content: b64 }],
    },
  });
  if (error) throw new Error(error.message || "Failed to send dashboard report email");
}
